/**
 * contentBuilder — sub-container do slice de conteúdo pedagógico.
 *
 * Agrupa: Avaliação (banco+matriz), Questão, Tentativa (read-only audit),
 * Docente (CRUD + assignments) e Método avaliativo.
 *
 * Extraído de `container.ts` na Sprint F.5-polish-extras-3.
 *
 * @since F.5-polish-extras-3
 */
import type { $Fetch } from 'ofetch';

import { HttpAvaliacaoGateway } from '@/infrastructure/http/HttpAvaliacaoGateway';
import { HttpMetodoAvaliativoGateway } from '@/infrastructure/http/HttpMetodoAvaliativoGateway';
import { HttpQuestaoGateway } from '@/infrastructure/http/HttpQuestaoGateway';
import { HttpTentativaGateway } from '@/infrastructure/http/HttpTentativaGateway';
import { HttpDocenteGateway } from '@/infrastructure/http/HttpDocenteGateway';

import {
  ListMetodosAvaliativos, CreateMetodoAvaliativo, UpdateMetodoAvaliativo, DeleteMetodoAvaliativo,
} from '@/application/usecase/MetodoAvaliativoUseCaseImpl';
import {
  ListAvaliacoes, GetAvaliacao, CreateAvaliacao, UpdateAvaliacao,
  PublishAvaliacao, ArchiveAvaliacao, DeleteAvaliacao,
  ListAvaliacaoQuestoes, AddQuestaoToAvaliacao, RemoveQuestaoFromAvaliacao,
} from '@/application/usecase/AvaliacaoUseCaseImpl';
import {
  ListQuestoes, GetQuestao, CreateQuestao, UpdateQuestao, DeleteQuestao,
} from '@/application/usecase/QuestaoUseCaseImpl';
import {
  ListTentativas, GetTentativa,
} from '@/application/usecase/TentativaUseCaseImpl';
import {
  ListDocentes, GetDocente, CreateDocente, UpdateDocente, ArchiveDocente,
  ListDocenteAssignments, AssignDocente, UnassignDocente,
} from '@/application/usecase/DocenteUseCaseImpl';

export function buildContentSlice(httpClient: $Fetch) {
  const avaliacaoGateway = new HttpAvaliacaoGateway(httpClient);
  const metodoAvaliativoGateway = new HttpMetodoAvaliativoGateway(httpClient);
  const questaoGateway = new HttpQuestaoGateway(httpClient);
  const tentativaGateway = new HttpTentativaGateway(httpClient);
  const docenteGateway = new HttpDocenteGateway(httpClient);

  const useCases = {
    listMetodosAvaliativos: new ListMetodosAvaliativos(metodoAvaliativoGateway),
    createMetodoAvaliativo: new CreateMetodoAvaliativo(metodoAvaliativoGateway),
    updateMetodoAvaliativo: new UpdateMetodoAvaliativo(metodoAvaliativoGateway),
    deleteMetodoAvaliativo: new DeleteMetodoAvaliativo(metodoAvaliativoGateway),
    listAvaliacoes: new ListAvaliacoes(avaliacaoGateway),
    getAvaliacao: new GetAvaliacao(avaliacaoGateway),
    createAvaliacao: new CreateAvaliacao(avaliacaoGateway),
    updateAvaliacao: new UpdateAvaliacao(avaliacaoGateway),
    publishAvaliacao: new PublishAvaliacao(avaliacaoGateway),
    archiveAvaliacao: new ArchiveAvaliacao(avaliacaoGateway),
    deleteAvaliacao: new DeleteAvaliacao(avaliacaoGateway),
    listAvaliacaoQuestoes: new ListAvaliacaoQuestoes(avaliacaoGateway),
    addQuestaoToAvaliacao: new AddQuestaoToAvaliacao(avaliacaoGateway),
    removeQuestaoFromAvaliacao: new RemoveQuestaoFromAvaliacao(avaliacaoGateway),
    listQuestoes: new ListQuestoes(questaoGateway),
    getQuestao: new GetQuestao(questaoGateway),
    createQuestao: new CreateQuestao(questaoGateway),
    updateQuestao: new UpdateQuestao(questaoGateway),
    deleteQuestao: new DeleteQuestao(questaoGateway),
    listTentativas: new ListTentativas(tentativaGateway),
    getTentativa: new GetTentativa(tentativaGateway),
    listDocentes: new ListDocentes(docenteGateway),
    getDocente: new GetDocente(docenteGateway),
    createDocente: new CreateDocente(docenteGateway),
    updateDocente: new UpdateDocente(docenteGateway),
    archiveDocente: new ArchiveDocente(docenteGateway),
    listDocenteAssignments: new ListDocenteAssignments(docenteGateway),
    assignDocente: new AssignDocente(docenteGateway),
    unassignDocente: new UnassignDocente(docenteGateway),
  };

  return { useCases };
}
